import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ShoppingBag } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { Product, PRODUCT_SELECT } from '../types/product';
import { productImage } from '../lib/productImage';
import { priceLabel } from '../lib/variants';
import { useSiteContent } from '../hooks/useSiteContent';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { richTextHtml } from '../lib/richText';
import { Button } from '../components/ui/button';
import { Reveal } from '../components/Reveal';
import headerImage from '../assets/images/arnie_campo.jpg';

/** "Perché il miele cristallizza" -> "perche-il-miele-cristallizza", per i link interni. */
const slug = (text: string) =>
  text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

/** Un paragrafo per ogni riga del testo scritto nella dashboard. */
const Paragraphs = ({ text }: { text: string }) => (
  <>
    {text
      .split('\n')
      .filter((line) => line.trim())
      .map((line, i) => (
        <p key={i}>{line}</p>
      ))}
  </>
);

/**
 * Pagina "Il nostro miele": lavorazione, cristallizzazione, conservazione,
 * calendario del raccolto e domande frequenti. Testi modificabili da
 * Contenuti → Il nostro miele; l'elenco dei mieli arriva dal catalogo.
 */
export default function Honey() {
  const { content: page, loaded } = useSiteContent('page.honey');
  const [products, setProducts] = useState<Product[]>([]);

  useDocumentMeta(page.title, page.intro);

  useEffect(() => {
    supabase
      .from('products')
      .select(PRODUCT_SELECT)
      .eq('active', true)
      .order('created_at', { ascending: true })
      .then(({ data, error }) => {
        if (error) console.error('Error fetching products:', error);
        setProducts(data || []);
      });
  }, []);

  return (
    // Invisibile finché il DB non risponde: niente lampo dei testi predefiniti.
    <div className={loaded ? undefined : 'invisible'}>
      <section className="relative overflow-hidden bg-[#2F1500]">
        {loaded && <img src={page.image ?? headerImage} alt="" className="absolute inset-0 h-full w-full object-cover" />}
        <div className="absolute inset-0 bg-gradient-to-t from-[#2F1500]/90 via-[#2F1500]/65 to-[#2F1500]/35" />
        <div className="container relative z-10 py-20 md:py-28">
          <Reveal className="max-w-3xl">
            <span className="mb-6 block h-1 w-12 rounded-full bg-[#FFB95F]" />
            <h1 className="font-display text-4xl font-bold leading-tight text-white text-balance md:text-5xl">{page.title}</h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/90 md:text-lg">{page.intro}</p>
          </Reveal>
        </div>
      </section>

      {page.sections.length > 1 && (
        <nav aria-label="In questa pagina" className="border-b border-border bg-muted/60">
          <div className="container flex gap-2 overflow-x-auto py-4">
            {page.sections.map((s) => (
              <a
                key={s.title}
                href={`#${slug(s.title)}`}
                className="flex-none whitespace-nowrap rounded-full bg-card px-4 py-2 text-sm font-semibold text-foreground shadow-sm ring-1 ring-border/60 hover:text-primary"
              >
                {s.title}
              </a>
            ))}
          </div>
        </nav>
      )}

      <div className="container max-w-3xl py-14">
        {page.sections.map((s) => (
          <section key={s.title} id={slug(s.title)} className="scroll-mt-24 border-b border-border py-10 first:pt-0 last:border-b-0">
            <h2 className="mb-5 font-display text-2xl font-semibold text-foreground md:text-3xl">{s.title}</h2>
            <div
              className="rich-text text-base leading-relaxed text-foreground/80 md:text-lg"
              dangerouslySetInnerHTML={{ __html: richTextHtml(s.text) }}
            />
            {s.images?.length > 0 && (
              <div className={`mt-8 grid gap-4 ${s.images.length > 1 ? 'sm:grid-cols-2' : ''}`}>
                {s.images.map((url, i) => (
                  <img
                    key={url}
                    src={url}
                    alt={s.images.length > 1 ? `${s.title} (foto ${i + 1})` : s.title}
                    loading="lazy"
                    className={`w-full rounded-2xl object-cover shadow-sm ${
                      s.images.length === 1 ? 'aspect-[16/9]' : 'aspect-[4/3]'
                    } ${s.images.length === 3 && i === 0 ? 'sm:col-span-2 sm:aspect-[16/9]' : ''}`}
                  />
                ))}
              </div>
            )}
          </section>
        ))}
      </div>

      {products.length > 0 && (
        <section className="bg-muted/60 py-16">
          <div className="container">
            <h2 className="mb-3 font-display text-3xl font-semibold text-foreground">I nostri mieli</h2>
            <p className="mb-10 max-w-2xl text-muted-foreground">Quelli che trovi adesso nel nostro negozio.</p>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {products.map((product, i) => (
                <Reveal key={product.id} index={i} className="h-full">
                  <Link
                    to={`/products/${product.id}`}
                    className="group flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-sm transition-shadow hover:shadow-lg"
                  >
                    <div className="h-44 overflow-hidden">
                      <img
                        src={productImage(product.image_url)}
                        alt={product.name}
                        loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                      />
                    </div>
                    <div className="flex flex-1 flex-col p-5">
                      <h3 className="mb-2 font-display text-lg font-semibold text-foreground">{product.name}</h3>
                      <p className="mb-4 line-clamp-3 flex-1 text-sm text-muted-foreground">{product.description}</p>
                      <span className="ap-price text-base font-bold text-primary">{priceLabel(product)}</span>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {page.faq.length > 0 && (
        <section className="container max-w-3xl py-16">
          <h2 className="mb-8 font-display text-3xl font-semibold text-foreground">Domande frequenti</h2>
          <div className="divide-y divide-border rounded-2xl border border-border bg-card">
            {page.faq.map((item) => (
              <details key={item.question} className="group px-6 py-5">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-semibold text-foreground [&::-webkit-details-marker]:hidden">
                  {item.question}
                  <ChevronDown className="h-5 w-5 flex-none text-muted-foreground transition-transform group-open:rotate-180" />
                </summary>
                <div className="mt-3 space-y-3 leading-relaxed text-muted-foreground">
                  <Paragraphs text={item.answer} />
                </div>
              </details>
            ))}
          </div>
        </section>
      )}

      <section className="bg-brand">
        <div className="container py-14 text-center">
          <h2 className="mb-6 font-display text-3xl font-bold text-white">Pronto ad assaggiarlo?</h2>
          <Button asChild size="lg" shape="pill" variant="accent">
            <Link to="/products">
              <ShoppingBag className="h-5 w-5" />
              Scopri i prodotti
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
